import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Same Material 3 palette as the session-timeout dialog (SEE-219 Figma).
const COLOR_TITLE = '#3A3B41';
const COLOR_SUPPORT = '#44474F';
const COLOR_PRIMARY = '#193059';
const COLOR_PRIMARY_HOVER = '#142747';

/**
 * SEE-219: shown after an inactivity logout, including in tabs (or the mobile
 * shell) that were left open overnight — the guard navigates here the moment
 * the idle window expires, so returning users see this message immediately
 * instead of a stale, dead dashboard. Deliberately rendered OUTSIDE
 * RequireAuth: it must keep displaying while the sign-out settles and never
 * bounce through a loading spinner.
 */
function SessionExpiredPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        // SEE-1715: less the mobile shell's status bar padding on #root.
        minHeight: 'calc(100vh - var(--native-safe-top, 0px))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', maxWidth: 360 }}
      >
        <Typography
          component="h1"
          sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 400, color: COLOR_TITLE, textAlign: 'center' }}
        >
          {t('sessionTimeout.loggedOutTitle', "You've been logged out")}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: '0.25px',
            color: COLOR_SUPPORT,
            textAlign: 'center',
          }}
        >
          {t(
            'sessionTimeout.loggedOutBody',
            'Your session ended due to inactivity. Sign in again to continue.'
          )}
        </Typography>
        <Button
          onClick={() => navigate('/login', { replace: true })}
          variant="contained"
          disableElevation
          sx={{
            mt: '8px',
            bgcolor: COLOR_PRIMARY,
            color: '#fff',
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 600,
            borderRadius: '12px',
            px: '24px',
            '&:hover': { bgcolor: COLOR_PRIMARY_HOVER },
          }}
        >
          {t('sessionTimeout.signInAgain', 'Sign in')}
        </Button>
      </Box>
    </Box>
  );
}

export default SessionExpiredPage;
